/**
 * Pure utility functions for timeline formatting and tick generation.
 * Extracted from timeline-bar.ts for reuse and testability.
 */

const DAY = 86_400_000

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/** Default tick spacing in pixels. */
export const TICK_SPACING_PX = 32

/**
 * Format a Date as a 12-hour time string, e.g. "3:05 PM".
 */
export function formatTime(date: Date): string {
  const h = date.getHours()
  const m = date.getMinutes().toString().padStart(2, '0')
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  return `${h12}:${m} ${ampm}`
}

/**
 * Format a Date as a short date string, e.g. "Jun 1, 2024".
 */
export function formatDateShort(date: Date): string {
  return `${MONTH_ABBR[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
}

/**
 * Convert a Date to "YYYY-MM-DD HH:MM" for display in a plain text input.
 */
export function toDatetimeLocalValue(date: Date): string {
  const y = date.getFullYear()
  const m = (date.getMonth() + 1).toString().padStart(2, '0')
  const d = date.getDate().toString().padStart(2, '0')
  const h = date.getHours().toString().padStart(2, '0')
  const min = date.getMinutes().toString().padStart(2, '0')
  return `${y}-${m}-${d}T${h}:${min}`
}

/**
 * Parse a "YYYY-MM-DD HH:MM" string (from text input) into a Date.
 * Returns null if the value cannot be parsed.
 */
export function parseDatetimeLocalValue(value: string): Date | null {
  const normalized = value.trim().replace(' ', 'T')
  const d = new Date(normalized)
  if (isNaN(d.getTime())) return null
  return d
}

/**
 * Format a tick label. For ranges >= 7 days, shows "Mon D". Otherwise shows
 * 12-hour time like "3PM" or "3:15PM".
 */
export function formatTickLabel(date: Date, rangeMs: number): string {
  if (rangeMs >= 7 * DAY) {
    return `${MONTH_ABBR[date.getMonth()]} ${date.getDate()}`
  }
  const h = date.getHours()
  const m = date.getMinutes()
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  if (m === 0) {
    return `${h12}${ampm}`
  }
  return `${h12}:${m.toString().padStart(2, '0')}${ampm}`
}

export interface Tick {
  fraction: number
  major: boolean
  label?: string | undefined
}

/**
 * Generate evenly-spaced ticks for a timeline axis.
 *
 * @param startTime   Left edge of the timeline
 * @param endTime     Right edge of the timeline
 * @param axisWidth   Width of the axis element in pixels
 * @param tickSpacingPx  Desired spacing between ticks in pixels (default: TICK_SPACING_PX)
 * @returns Array of tick objects with fraction (0-1), major flag, and optional label
 */
export function generateTicks(
  startTime: Date,
  endTime: Date,
  axisWidth: number,
  tickSpacingPx: number = TICK_SPACING_PX
): Tick[] {
  const rangeMs = endTime.getTime() - startTime.getTime()
  if (rangeMs <= 0) {
    return []
  }

  const totalTicks = Math.max(2, Math.floor(axisWidth / tickSpacingPx))

  // ~1 label per 80px, minimum 3
  const maxLabels = Math.max(3, Math.floor(axisWidth / 80))
  const labelEvery = Math.max(1, Math.ceil(totalTicks / maxLabels))

  const ticks: Tick[] = []
  for (let i = 0; i <= totalTicks; i++) {
    const fraction = i / totalTicks
    const timeMs = startTime.getTime() + rangeMs * fraction
    const isMajor = i % labelEvery === 0
    // Label major ticks, but skip first (crowded by time label) and last
    const showLabel = isMajor && i > 0 && i < totalTicks
    const label = showLabel ? formatTickLabel(new Date(timeMs), rangeMs) : undefined
    ticks.push({ fraction, major: isMajor, label })
  }

  return ticks
}

/**
 * Compute the needle position as a fraction (0-1) within the timeline range.
 *
 * @param currentTime  The current playback time
 * @param startTime    Start of the timeline range
 * @param endTime      End of the timeline range
 * @returns A number between 0 and 1 (not clamped — caller may get values outside 0-1)
 */
export function getNeedleFraction(currentTime: Date, startTime: Date, endTime: Date): number {
  const range = endTime.getTime() - startTime.getTime()
  if (range <= 0) {
    return 0
  }
  return (currentTime.getTime() - startTime.getTime()) / range
}
